import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Upload, Download, Shield, Zap, Clock, Users, 
  Lock, Globe, Smartphone, CheckCircle, ArrowRight,
  QrCode, Mail, Eye, Palette, FileArchive, Bell, Star, Loader2, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = { Upload, Download, Shield, Zap, Clock, Users, Lock, Globe, Smartphone, CheckCircle, QrCode, Mail, Eye, Palette, FileArchive, Bell, Star };
const colorClasses = {
  blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  green: 'bg-green-500/20 text-green-400 border-green-500/30',
  yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  pink: 'bg-pink-500/20 text-pink-400 border-pink-500/30'
};

const FALLBACK_FEATURES = [
  { icon: 'Upload', title: 'Envoi de fichiers volumineux', description: 'Partagez jusqu\'a 20 Go de fichiers en quelques clics.', color: 'blue', details: ['Jusqu\'a 20 Go par transfert', 'Multi-fichiers supportes', 'Compression ZIP automatique', 'Telechargement illimite'] },
  { icon: 'Shield', title: 'Securite & Chiffrement', description: 'Vos fichiers sont proteges par un chiffrement de bout en bout.', color: 'green', details: ['Chiffrement AES-256', 'Protection par mot de passe', 'Aucun acces serveur', 'Conformite RGPD', 'Stockage en France / Europe'] },
  { icon: 'Clock', title: 'Expiration automatique', description: 'Les liens de telechargement expirent automatiquement.', color: 'yellow', details: ['7 jours (gratuit)', 'Personnalisable (Pro)', 'Suppression automatique', 'Aucune trace'] },
  { icon: 'QrCode', title: 'QR Code & Partage facile', description: 'Generez un QR code pour chaque transfert.', color: 'purple', details: ['QR code instantane', 'Partage par email', 'Lien court', 'Copie en un clic'] },
  { icon: 'Bell', title: 'Notifications de telechargement', description: 'Soyez averti quand vos fichiers sont telecharges.', color: 'cyan', details: ['Email de confirmation', 'Statistiques detaillees', 'Historique complet', 'Tableau de bord'] },
  { icon: 'Palette', title: 'Personnalisation Pro', description: 'Personnalisez l\'experience avec des fonds d\'ecran et votre branding.', color: 'pink', details: ['Fonds personnalises', 'Logo entreprise', 'Couleurs sur mesure', 'Message d\'accueil'] },
  { icon: 'Zap', title: 'Transfert P2P (Ultimate)', description: 'Transfert direct navigateur-a-navigateur sans passer par nos serveurs.', color: 'purple', details: ['WebRTC direct', 'Aucune limite de taille', 'Pas de stockage serveur', 'Partage par lien ou QR code'] }
];

const FALLBACK_COMPARISON = [
  ['Taille max par fichier', '20 Go', 'Illimitee', 'Illimitee + P2P'],
  ['Nombre de fichiers', '5 fichiers', 'Illimite', 'Illimite'],
  ['Duree d\'expiration', '7 jours', 'Personnalisable', '60 jours'],
  ['Protection mot de passe', '\u2713', '\u2713', '\u2713'],
  ['Programmation d\'envoi', '\u2713', '\u2713', '\u2713'],
  ['QR Code', '\u2713', '\u2713', '\u2713'],
  ['Chiffrement E2E', '\u2713', '\u2713', '\u2713'],
  ['Stockage France / Europe', '\u2713', '\u2713', '\u2713'],
  ['Notifications email', '\u2014', '\u2713', '\u2713'],
  ['Statistiques detaillees', '\u2014', '\u2713', '\u2713'],
  ['Fond personnalise', '\u2014', '\u2713', '\u2713'],
  ['Vente de fichiers', '\u2014', '\u2713', '\u2713'],
  ['Transfert P2P (WebRTC)', '\u2014', '\u2014', '\u2713'],
  ['Support prioritaire', '\u2014', '\u2713', '\u2713'],
];

const FeaturesPage = () => {
  const [features, setFeatures] = useState(FALLBACK_FEATURES);
  const [comparison, setComparison] = useState(FALLBACK_COMPARISON);
  const [hero, setHero] = useState({ title: 'Tout ce dont vous avez besoin', subtitle: 'pour partager vos fichiers', badge: 'Fonctionnalités complètes', description: 'SENDBOX combine simplicité, sécurité et performance pour vous offrir la meilleure expérience de partage de fichiers.' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCMS = async () => {
      try {
        const res = await axios.get(`${API}/pages/features`);
        const page = res.data;
        for (const section of page.sections || []) {
          if (section.type === 'hero') {
            setHero(prev => ({ ...prev, ...section }));
          }
          if (section.type === 'features_grid' && section.items?.length) {
            const items = section.items;
            // Ensure P2P feature card is always present
            const hasP2P = items.some(f => f.title?.toLowerCase().includes('p2p'));
            if (!hasP2P) {
              items.push({ icon: 'Zap', title: 'Transfert P2P (Ultimate)', description: 'Transfert direct navigateur-a-navigateur sans passer par nos serveurs.', color: 'purple', details: ['WebRTC direct', 'Aucune limite de taille', 'Pas de stockage serveur', 'Partage par lien ou QR code'] });
            }
            setFeatures(items);
          }
          if (section.type === 'comparison' && section.rows?.length) {
            const rows = section.rows;
            // Ensure P2P row is always present with Ultimate differentiation
            const hasP2PRow = rows.some(r => r[0]?.toLowerCase().includes('p2p'));
            if (!hasP2PRow) {
              rows.push(['Transfert P2P (WebRTC)', '\u2014', '\u2014', '\u2713']);
            }
            // Ensure all rows have 4 columns (add Ultimate column if missing)
            const fixedRows = rows.map(r => r.length < 4 ? [...r, r[2]] : r);
            setComparison(fixedRows);
          }
        }
      } catch {}
      setLoading(false);
    };
    fetchCMS();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <NavigationMenu />
      <main className="pt-24 pb-32">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-300 text-sm mb-6">
            <Zap className="w-4 h-4" />
            {hero.badge}
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="features-title">
            {hero.title}<br />
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              {hero.subtitle}
            </span>
          </h1>
          <p className="text-lg md:text-xl text-blue-200/70 max-w-3xl mx-auto">{hero.description}</p>
        </section>

        {/* Features Grid */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => {
              const Icon = iconMap[feature.icon] || Star;
              return (
                <div key={idx} className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 hover:bg-slate-800/70 hover:border-blue-500/30 transition-all group" data-testid={`feature-card-${idx}`}>
                  <div className={`w-14 h-14 rounded-xl ${colorClasses[feature.color] || colorClasses.blue} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                  <p className="text-blue-200/60 leading-relaxed mb-4">{feature.description}</p>
                  {feature.details && (
                    <ul className="space-y-2">
                      {feature.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-blue-300/70">
                          <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Comparison Table */}
        <section className="max-w-5xl mx-auto px-6 mb-20">
          <h2 className="text-3xl font-bold text-white text-center mb-10">Gratuit vs Pro vs Ultimate</h2>
          <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-4">
              <div className="text-left p-4 text-blue-300/70 font-medium border-b border-blue-500/20">Fonctionnalite</div>
              <div className="text-center p-4 text-white font-medium border-b border-blue-500/20">Gratuit</div>
              <div className="text-center p-4 text-cyan-400 font-medium border-b border-blue-500/20 flex items-center justify-center gap-2"><Crown className="w-4 h-4 text-yellow-400" /> Pro</div>
              <div className="text-center p-4 text-purple-400 font-medium border-b border-blue-500/20 flex items-center justify-center gap-2"><Zap className="w-4 h-4 text-purple-400" /> Ultimate</div>
            </div>
            <div className="text-sm">
              {comparison.map((row, idx) => (
                <div key={idx} className={`grid grid-cols-4 ${idx < comparison.length - 1 ? 'border-b border-blue-500/10' : ''}`}>
                  <div className="p-4 text-blue-200/80">{row[0]}</div>
                  <div className="p-4 text-center text-blue-300/60">{row[1]}</div>
                  <div className="p-4 text-center text-cyan-300">{row[2]}</div>
                  <div className="p-4 text-center text-purple-300">{row[3] || row[2]}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* France/Europe Storage Banner */}
        <section className="max-w-4xl mx-auto px-6 mb-20">
          <div className="bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-500/20 rounded-2xl p-8 flex items-center gap-6">
            <div className="flex-shrink-0 w-16 h-16 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-center text-3xl">
              <Shield className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-1" data-testid="france-storage-title">Vos fichiers sont stockes en France et en Europe</h3>
              <p className="text-blue-200/70 text-sm leading-relaxed">
                Tous les fichiers transférés via SENDBOX sont hébergés sur des serveurs situés en France et en Europe, garantissant une conformité totale avec le RGPD et la souveraineté numérique de vos données.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-cyan-500/30 rounded-3xl p-10">
            <h2 className="text-3xl font-bold text-white mb-4">Prêt à partager vos fichiers ?</h2>
            <p className="text-blue-200/70 mb-8 max-w-xl mx-auto">Commencez gratuitement, sans inscription. Passez Pro quand vous en avez besoin.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/"><Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"><Upload className="w-5 h-5 mr-2" />Envoyer des fichiers</Button></Link>
              <Link to="/pricing"><Button size="lg" variant="outline" className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10">Voir les tarifs Pro<ArrowRight className="w-5 h-5 ml-2" /></Button></Link>
            </div>
          </div>
        </section>
      </main>
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default FeaturesPage;
